import { useState, useEffect } from "react";
import { fetchProductReviewDetails } from "../services/productsService";

/**
 * Fetches the full review details for a product using
 * GET /api/v1/admin/products/{product_id}/review-details
 *
 * Works for products in any status (pending, approved, rejected).
 *
 * @param {string|null} productId - The product id from the products list
 *
 * @returns {{
 *   detail: object|null,
 *   loading: boolean,
 *   error: string|null
 * }}
 */
export function useReviewDetail(productId, ageGroup) {
  const [detail, setDetail]   = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);

  useEffect(() => {
    if (!productId) return;

    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);
      try {
        const data = await fetchProductReviewDetails(productId, ageGroup);
        if (!cancelled) setDetail(data);
      } catch (err) {
        if (!cancelled) setError(err.message || "Failed to load product details");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [productId, ageGroup]);

  return { detail, loading, error };
}

/**
 * Derives a stable list of image entries from a loaded review detail.
 *
 * The API returns files as plain metadata objects (file_id, file_type,
 * file_name, mime_type) – not URLs.  We map each to a display entry that
 * UI components can render as an emoji placeholder until real signed URLs
 * are available.
 *
 * File type → emoji mapping:
 *   front_label       → 📦
 *   nutrition_table   → 📊
 *   ingredients_label → 📋
 *   (anything else)   → 🔍
 *
 * @param {object|null} detail - The raw review detail from the API
 * @returns {Array<{ fileId: string, fileType: string, fileName: string, emoji: string, bg: string }>}
 */
export function parseReviewFiles(detail) {
  if (!detail) return [];
  const files = detail.submission?.files ?? [];
  if (!files.length) return [];

  const emojiMap = {
    front_label:       { emoji: "📦", bg: "rgba(55,190,176,.12)" },
    nutrition_table:   { emoji: "📊", bg: "rgba(12,97,112,.08)"  },
    ingredients_label: { emoji: "📋", bg: "rgba(164,229,224,.15)" },
  };

  return files.map((f) => {
    const { emoji, bg } = emojiMap[f.file_type] || { emoji: "🔍", bg: "rgba(55,190,176,.12)" };
    return {
      fileId:   f.file_id,
      fileType: f.file_type,
      fileName: f.file_name,
      mimeType: f.mime_type,
      url:      f.url,
      emoji,
      bg,
    };
  });
}

/**
 * Parses the OCR ingredients array from the API into a sorted list of
 * plain ingredient names.
 *
 * API shape: [{ position, name, confidence }, ...]
 *
 * @param {Array<{position:number, name:string, confidence:number}>|null} items
 * @returns {Array<{position:number, name:string, confidence:number}>}
 */
export function parseOcrIngredients(items) {
  if (!items) return [];
  if (Array.isArray(items)) {
    return [...items].sort((a, b) => (a.position || 0) - (b.position || 0));
  }
  if (typeof items === "string") {
    return items.split(",").map((s, idx) => ({ position: idx + 1, name: s.trim() })).filter(i => i.name);
  }
  return [];
}

/**
 * Parses the OCR nutrition_facts array or object from the API into a sorted list
 * of nutrient rows ready for table rendering.
 *
 * @param {Array|Object|null} items
 * @returns {Array<{nutrient_key:string, label:string, value:number, unit:string, confidence:number}>}
 */
export function parseOcrNutrition(items) {
  if (!items) return [];

  let arrayItems = [];
  if (Array.isArray(items)) {
    arrayItems = items;
  } else if (typeof items === "object") {
    arrayItems = Object.entries(items).map(([k, v]) => ({
      nutrient_key: k,
      value: v?.value ?? v,
      unit: v?.unit || "g",
      basis: v?.basis || "100g",
      confidence: v?.confidence,
      detail_concern: v?.detail_concern,
    }));
  }

  if (arrayItems.length === 0) return [];

  // Keep only the first occurrence of each unique nutrient_key
  const seen = new Set();
  const uniqueItems = [];
  for (const item of arrayItems) {
    if (item && item.nutrient_key && !seen.has(item.nutrient_key)) {
      seen.add(item.nutrient_key);
      uniqueItems.push(item);
    }
  }

  // Human-readable label overrides
  const LABELS = {
    energy:        "Energy",
    carbohydrate:  "Carbohydrate",
    sugar:         "of which Sugars",
    fat:           "Fat",
    saturated_fat: "of which Saturates",
    protein:       "Protein",
    sodium:        "Sodium",
    fibre:         "Dietary Fibre",
    salt:          "Salt",
  };

  return uniqueItems.map((row) => ({
    ...row,
    label: LABELS[row.nutrient_key] || row.nutrient_key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()),
  }));
}
